import React, { useState } from 'react';
import { Observer } from 'mobx-react';
import styled from '@emotion/styled/macro';
import { colors } from '../../../common/styles/theme.styles';
import { BaseModal } from '../../../common/components/BaseModal/BaseModal';

const ModalContent = styled.div`
  padding: 24px;
  max-width: 500px;
`;

const Title = styled.h2`
  margin: 0 0 20px;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.gray1};
`;

const FormField = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.gray1};
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.gray3};
  border-radius: 6px;
  font-size: 14px;
  background-color: #ffffff;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${colors.blue};
    box-shadow: 0 0 0 3px ${colors.blue}33;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${colors.gray1};
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: 1px solid ${colors.gray3};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &.primary {
    background-color: ${colors.blue};
    color: #ffffff;
    border-color: ${colors.blue};

    &:hover {
      background-color: #1d4ed8;
      border-color: #1d4ed8;
    }
  }

  &.secondary {
    background-color: #ffffff;
    color: ${colors.gray1};

    &:hover {
      background-color: ${colors.gray4};
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviewSection = styled.div`
  margin: 20px 0;
  padding: 16px;
  border: 1px solid ${colors.gray3};
  border-radius: 6px;
  background-color: ${colors.gray4};
  text-align: center;
`;

const PreviewText = styled.div`
  font-size: 12px;
  color: ${colors.gray2};
  margin-bottom: 8px;
`;

const PreviewBox = styled.div`
  height: 120px;
  background-color: #ffffff;
  border: 1px solid ${colors.gray3};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.gray2};
  font-size: 14px;
`;

export const EntityStructureReport = ({
  entities = [],
  isOpen = false,
  onClose,
}) => {
  const [rootEntityId, setRootEntityId] = useState(entities[0]?.id || '');
  const [layout, setLayout] = useState('horizontal');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownloadSample = () => {
    // Generate a sample report
    console.log('Downloading sample report...');
    
    // Create a simple canvas-based sample
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    
    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Title
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Entity Structure Report - Sample', canvas.width / 2, 40);
    
    // Sample entity nodes
    const entities = ['Parent Entity', 'Subsidiary A', 'Subsidiary B'];
    const positions = layout === 'horizontal' 
      ? [[400, 150], [200, 300], [600, 300]]
      : [[400, 150], [400, 300], [400, 450]];
    
    entities.forEach((entity, index) => {
      const [x, y] = positions[index];
      
      // Entity box
      ctx.fillStyle = '#2563eb';
      ctx.fillRect(x - 80, y - 30, 160, 60);
      
      // Entity text
      ctx.fillStyle = '#ffffff';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(entity, x, y + 5);
      
      // Connection lines
      if (index > 0) {
        ctx.strokeStyle = '#6b7280';
        ctx.lineWidth = 2;
        ctx.beginPath();
        if (layout === 'horizontal') {
          ctx.moveTo(positions[0][0], positions[0][1] + 30);
          ctx.lineTo(x, y - 30);
        } else {
          ctx.moveTo(positions[index - 1][0], positions[index - 1][1] + 30);
          ctx.lineTo(x, y - 30);
        }
        ctx.stroke();
        
        // Ownership percentage
        ctx.fillStyle = '#374151';
        ctx.font = '12px Arial';
        const midX = (positions[0][0] + x) / 2;
        const midY = (positions[0][1] + y) / 2;
        ctx.fillText('100%', midX, midY);
      }
    });
    
    // Convert to blob and download
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'entity-structure-sample.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  const handleDownloadStructure = async () => {
    setIsGenerating(true);
    
    try {
      // TODO: Implement actual structure report generation
      // This would integrate with the graph rendering
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Generating structure report for:', {
        rootEntityId,
        layout,
        entities: entities.length
      });
      
      // For now, just download the sample
      handleDownloadSample();
      
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const rootEntity = entities.find(e => e.id.toString() === rootEntityId);

  return (
    <Observer>
        <BaseModal
          visible={isOpen}
          onClose={onClose}
          title=""
          footer={null}
          width={550}
        >
          <ModalContent>
            <Title>Generate Entity Structure Report</Title>
            
            <FormField>
              <Label>Root Entity</Label>
              <Select
                value={rootEntityId}
                onChange={(e) => setRootEntityId(e.target.value)}
              >
                {entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField>
              <Label>Layout</Label>
              <RadioGroup>
                <RadioOption>
                  <input
                    type="radio"
                    name="layout"
                    value="horizontal"
                    checked={layout === 'horizontal'}
                    onChange={(e) => setLayout(e.target.value)}
                  />
                  Horizontal
                </RadioOption>
                <RadioOption>
                  <input
                    type="radio"
                    name="layout"
                    value="vertical"
                    checked={layout === 'vertical'}
                    onChange={(e) => setLayout(e.target.value)}
                  />
                  Vertical
                </RadioOption>
              </RadioGroup>
            </FormField>

            <PreviewSection>
              <PreviewText>Report Preview ({layout} layout)</PreviewText>
              <PreviewBox>
                {rootEntity ? (
                  `Structure starting from "${rootEntity.name}"`
                ) : (
                  'Select a root entity to preview'
                )}
              </PreviewBox>
            </PreviewSection>

            <ButtonGroup>
              <Button className="secondary" onClick={handleDownloadSample}>
                Download Sample
              </Button>
              <Button 
                className="primary" 
                onClick={handleDownloadStructure}
                disabled={!rootEntityId || isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Download Structure'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </BaseModal>
    </Observer>
  );
}; 